import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MailCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function GoogleButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid="google-login-btn"
      className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-card py-2.5 text-sm font-bold transition-colors hover:bg-accent">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.19 3.32v2.77h3.54c2.08-1.92 3.29-4.74 3.29-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.54-2.77c-.98.66-2.23 1.06-3.74 1.06-2.87 0-5.3-1.94-6.16-4.55H2.18v2.86A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.86Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.86C6.7 7.32 9.13 5.38 12 5.38Z"/></svg>
      Continue with Google
    </button>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col p-6 sm:p-10">
        <div className="flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm animate-fade-up">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">{title}</h1>
            <p className="mt-2 text-muted-foreground">{subtitle}</p>
            <div className="mt-8">{children}</div>
          </div>
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-[#09090B] lg:block">
        <div className="absolute -left-20 top-20 h-96 w-96 rounded-full bg-brand/30 blur-3xl" />
        <img src="https://images.unsplash.com/photo-1553531889-56cc480ac5cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
          alt="Happy customer" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <p className="font-heading text-3xl font-extrabold leading-tight">Your receipts, warranties and proofs — safe in one place.</p>
          <p className="mt-3 max-w-md text-white/70">Join thousands of shoppers and retailers going paperless with ProofPass.</p>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      const home = u.role === "admin" ? "/admin" : u.role === "retailer" ? "/retailer" : "/wallet";
      toast.success("Welcome back!");
      navigate(home, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your ProofPass wallet.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="mt-1.5" />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-brand text-white hover:bg-brand-hover">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm font-semibold text-brand hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton onClick={loginWithGoogle} />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        New here? <Link to="/register" className="font-bold text-brand hover:underline">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      const home = u.role === "retailer" ? "/retailer" : "/wallet";
      toast.success("Account created!");
      navigate(home, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start collecting digital proof in minutes.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label>I am a…</Label>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {[["customer", "Shopper"], ["retailer", "Retailer"]].map(([val, label]) => (
              <button key={val} type="button" data-testid={`role-${val}`} onClick={() => setForm((f) => ({ ...f, role: val }))}
                className={`rounded-lg border px-3 py-2.5 text-sm font-bold transition-colors ${form.role === val ? "border-brand bg-brand-muted text-brand" : "border-border hover:bg-accent"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required placeholder="Jane Smith" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="remail">Email</Label>
          <Input id="remail" type="email" data-testid="register-email" value={form.email} onChange={set("email")} required placeholder="you@example.com" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="rpass">Password</Label>
          <Input id="rpass" type="password" data-testid="register-password" value={form.password} onChange={set("password")} required minLength={6} placeholder="At least 6 characters" className="mt-1.5" />
        </div>
        <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-brand text-white hover:bg-brand-hover">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton onClick={loginWithGoogle} />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account? <Link to="/login" className="font-bold text-brand hover:underline">Sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.reset_link) setDevLink(data.reset_link);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll create a secure reset link.">
      {sent ? (
        <div className="animate-fade-up rounded-xl border border-border bg-card p-6 text-center" data-testid="forgot-sent">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-success/12 text-success"><MailCheck className="h-6 w-6" /></div>
          <p className="font-heading text-lg font-bold">Check your inbox</p>
          <p className="mt-1 text-sm text-muted-foreground">If an account exists for <b>{email}</b>, a reset link has been created.</p>
          {devLink && (
            <div className="mt-4 rounded-lg bg-secondary p-3 text-left">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Dev mode — no email service</p>
              <Link to={devLink} className="break-all text-sm font-semibold text-brand hover:underline" data-testid="dev-reset-link">{devLink}</Link>
            </div>
          )}
          <Link to="/login" className="mt-5 inline-block text-sm font-bold text-brand hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="femail">Email</Label>
            <Input id="femail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="mt-1.5" data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-brand text-white hover:bg-brand-hover" data-testid="forgot-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
          </Button>
          <p className="text-center text-sm text-muted-foreground"><Link to="/login" className="font-bold text-brand hover:underline">Back to sign in</Link></p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — you can sign in now.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This reset link is missing or malformed.">
        <Link to="/forgot-password" className="font-bold text-brand hover:underline">Request a new reset link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter a new password for your account.">
      {done ? (
        <div className="animate-fade-up rounded-xl border border-border bg-card p-6 text-center" data-testid="reset-done">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-success/12 text-success"><CheckCircle2 className="h-6 w-6" /></div>
          <p className="font-heading text-lg font-bold">Password updated</p>
          <p className="mt-1 text-sm text-muted-foreground">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="np">New password</Label>
            <Input id="np" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" className="mt-1.5" data-testid="reset-password" />
          </div>
          <div>
            <Label htmlFor="cp">Confirm password</Label>
            <Input id="cp" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter password" className="mt-1.5" data-testid="reset-confirm" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-brand text-white hover:bg-brand-hover" data-testid="reset-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
